'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Chat } from '../chat';
import { useChatContext } from '../context/chat-context';
import { getWidgetConfig, hasFullscreen } from './message/widget-registry';

export function FullscreenModal() {
  const { t } = useTranslation('chat2');
  const { fullscreenWidget, setFullscreenWidget } = useChatContext();
  const renderFullscreenContent = () => {
    if (!fullscreenWidget) return null;
    if (!hasFullscreen(fullscreenWidget.toolName)) return null;
    const widgetConfig = getWidgetConfig(fullscreenWidget.toolName);
    if (!widgetConfig) return null;
    return widgetConfig.renderFullscreen(fullscreenWidget.result, fullscreenWidget.args);
  };

  return (
    <AnimatePresence>
      {fullscreenWidget && (
        <motion.div
          className="flex flex-row h-dvh w-dvw fixed top-0 left-0 z-50 bg-transparent"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { delay: 0.2 } }}
        >
          <motion.div
            className="relative w-[400px] bg-muted dark:bg-background h-dvh shrink-0"
            initial={{ opacity: 0, x: -20, scale: 0.95 }}
            animate={{
              opacity: 1,
              x: 0,
              scale: 1,
              transition: {
                delay: 0.1,
                type: 'spring',
                stiffness: 200,
                damping: 30,
              },
            }}
            exit={{
              opacity: 0,
              x: -20,
              scale: 0.95,
              transition: { duration: 0.2 },
            }}
          >
            <div className="flex flex-col h-full">
              <Chat />
            </div>
          </motion.div>

          <motion.div
            className="fixed bg-background h-dvh flex flex-col overflow-y-auto border-l dark:border-zinc-700 border-zinc-200"
            initial={{ opacity: 0, x: 20, scale: 0.95 }}
            animate={{
              opacity: 1,
              x: 0,
              scale: 1,
              transition: {
                delay: 0.2,
                type: 'spring',
                stiffness: 200,
                damping: 30,
              },
            }}
            exit={{
              opacity: 0,
              x: 20,
              scale: 0.95,
              transition: { duration: 0.2 },
            }}
            style={{
              left: '400px',
              right: 0,
              width: 'calc(100vw - 400px)',
            }}
          >
            <div className="p-4 py-2 bg-card border-b">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-xl font-bold">{fullscreenWidget.title}</p>
                  <p className="text-sm text-muted-foreground">{t('fullscreen.view')}</p>
                </div>
                <button
                  onClick={() => setFullscreenWidget(null)}
                  className="p-2 hover:bg-muted rounded-md transition-colors"
                >
                  <XIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-auto p-6">{renderFullscreenContent()}</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
